/*
 * VM plus — aanvullingen na de livegang (gedeeld door VM, OB en LH).
 * - Productkaart met keuzelijsten (<vm-card-buy>): andere kleur kiezen = foto en link gaan mee.
 * - Kleurfoto's van een kaart alvast laden zodra iemand de kleurkeuze aanraakt (geen witte flits).
 * - Productpagina: kleur kiezen = de fotogalerij springt naar de foto van die kleur (volgorde blijft gelijk).
 * - Foto vergroten op de productpagina: één foto per scherm, pijlen/toetsen (desktop) en swipen (mobiel),
 *   zonder automatisch inzoomen.
 */
(() => {
  if (window.__vmPlus) return;
  window.__vmPlus = true;

  const COLOR_NAMES = ['color', 'colour', 'colore', 'kleur', 'couleur', 'farbe'];
  const productCache = new Map();

  function loadProduct(url) {
    const handle = (String(url || '').match(/\/products\/([^/?#]+)/) || [])[1];
    if (!handle) return Promise.resolve(null);
    if (!productCache.has(handle)) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      productCache.set(
        handle,
        fetch(`${root}products/${handle}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  }

  function showMedia(card, mediaId) {
    if (!card || !mediaId) return;
    const slideshow = /** @type {any} */ (card.querySelector('slideshow-component'));
    if (!slideshow) return;
    const slides = slideshow.refs?.slides || [...slideshow.querySelectorAll('slideshow-slide')];
    let found = false;
    for (const slide of slides) {
      if (slide.getAttribute('slide-id') === String(mediaId)) found = true;
    }
    if (!found) return;
    for (const slide of slides) {
      if (slide.getAttribute('variant-image') == null) continue;
      slide.hidden = slide.getAttribute('slide-id') !== String(mediaId);
    }
    if (typeof slideshow.select === 'function') {
      try {
        slideshow.select({ id: String(mediaId) }, undefined, { animate: false });
      } catch (_) {}
    }
  }

  function setLinks(card, variantId) {
    if (!card || !variantId) return;
    card.querySelectorAll('a[href]').forEach((link) => {
      try {
        const url = new URL(link.getAttribute('href') || '', window.location.origin);
        if (!url.pathname.includes('/products/')) return;
        url.searchParams.set('variant', String(variantId));
        link.setAttribute('href', url.pathname + url.search);
      } catch (_) {}
    });
    card.querySelectorAll('[data-vm-cqa]').forEach((button) => {
      if (button instanceof HTMLElement) button.dataset.variantId = String(variantId);
    });
  }

  function preloadCard(card) {
    if (!card || card.dataset.vmPreloaded) return;
    card.dataset.vmPreloaded = '1';
    card.querySelectorAll('slideshow-slide[variant-image] img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }

  ['pointerdown', 'focusin'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const trigger = target?.closest('.vm-cc__toggle, vm-card-buy select');
        if (trigger) preloadCard(trigger.closest('product-card'));
      },
      { capture: true, passive: true }
    );
  });

  /* ---------------------------------------------------------------- */
  /* Productpagina: fotogalerij start op de foto van de gekozen kleur   */
  /* ---------------------------------------------------------------- */

  // Het thema geeft de galerij een startfoto mee (initial-slide). Horizon zet dan wel de teller,
  // maar scrollt er niet naartoe; dat doen we hier, ook na het wisselen van kleur.
  function startAtInitialSlide(slideshow, tries = 0) {
    if (!slideshow.isConnected || slideshow.dataset.vmStartDone) return;
    const index = parseInt(slideshow.getAttribute('initial-slide') || '0', 10);
    if (!index) return;
    const scroller = slideshow.refs?.scroller;
    const slide = slideshow.refs?.slides?.[index];
    const id = slide?.getAttribute('slide-id');
    const ready = scroller && id && typeof slideshow.select === 'function';
    if (ready && !scroller.clientWidth) return; // galerij verborgen (bv. raster op desktop)
    if (ready && Math.round(scroller.scrollLeft / scroller.clientWidth) === index) {
      slideshow.dataset.vmStartDone = '1'; // staat goed: klaar
      return;
    }
    if (ready) {
      try {
        slideshow.current = 0;
        slideshow.select({ id }, undefined, { animate: false });
      } catch (_) {}
    }
    if (tries < 12) setTimeout(() => startAtInitialSlide(slideshow, tries + 1), ready ? 80 : 50);
  }

  function startGalleries(root) {
    const list = root.matches?.('media-gallery slideshow-component[initial-slide]')
      ? [root]
      : root.querySelectorAll?.('media-gallery slideshow-component[initial-slide]') || [];
    list.forEach((slideshow) => {
      // Zelf bladeren gaat altijd voor
      const stop = () => (slideshow.dataset.vmStartDone = '1');
      slideshow.addEventListener('pointerdown', stop, { once: true });
      slideshow.addEventListener('keydown', stop, { once: true });
      requestAnimationFrame(() => startAtInitialSlide(slideshow));
    });
  }

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element && (node.matches('media-gallery') || node.querySelector('media-gallery'))) startGalleries(node);
      });
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => startGalleries(document), { once: true });
  } else {
    startGalleries(document);
  }

  /* ---------------------------------------------------------------- */
  /* Vergrote foto's (zoom-dialog van Horizon)                         */
  /* ---------------------------------------------------------------- */

  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const openZoomDialog = () => document.querySelector('dialog.dialog-zoomed-gallery__dialog[open]');

  function zoomParts(dialog) {
    const zoom = /** @type {any} */ (dialog?.closest('zoom-dialog'));
    const list = dialog?.querySelector('.dialog-zoomed-gallery');
    const items = list ? [...list.children] : [];
    return { zoom, list, items };
  }

  function currentZoomIndex(dialog) {
    const { list, items } = zoomParts(dialog);
    if (!list || !items.length) return 0;
    if (isMobile()) return Math.round(list.scrollLeft / Math.max(1, list.clientWidth));
    return Math.round(dialog.scrollTop / Math.max(1, dialog.clientHeight));
  }

  function markThumbnail(dialog, index) {
    const { zoom, items } = zoomParts(dialog);
    dialog.querySelectorAll('.dialog-thumbnails-list__thumbnail').forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });
    if (zoom && typeof zoom.loadHighResolutionImage === 'function' && items[index]) {
      try { zoom.loadHighResolutionImage(items[index]); } catch (_) {}
    }
    const prev = dialog.querySelector('.vm-zoom-nav--prev');
    const next = dialog.querySelector('.vm-zoom-nav--next');
    if (prev instanceof HTMLButtonElement) prev.disabled = index <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = index >= items.length - 1;
  }

  function goZoom(dialog, step) {
    const { zoom, list, items } = zoomParts(dialog);
    if (!items.length) return;
    const index = Math.min(items.length - 1, Math.max(0, currentZoomIndex(dialog) + step));
    if (isMobile() && list) {
      list.scrollTo({ left: index * list.clientWidth, behavior: 'smooth' });
    } else if (zoom && typeof zoom.selectThumbnail === 'function') {
      zoom.selectThumbnail(index, { behavior: 'smooth' });
    } else {
      items[index].scrollIntoView({ behavior: 'smooth' });
    }
    markThumbnail(dialog, index);
  }

  function addZoomArrows(dialog) {
    if (!dialog || dialog.querySelector('.vm-zoom-nav')) return;
    const { items } = zoomParts(dialog);
    if (items.length < 2) return;
    const arrow = (dir) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `vm-zoom-nav vm-zoom-nav--${dir}`;
      button.setAttribute('aria-label', dir === 'prev' ? '‹' : '›');
      button.innerHTML = dir === 'prev'
        ? '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>'
        : '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        goZoom(dialog, dir === 'prev' ? -1 : 1);
      });
      return button;
    };
    dialog.append(arrow('prev'), arrow('next'));
  }

  // Dialoog open → pijlen toevoegen en de juiste stand zetten
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const dialog = mutation.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('dialog-zoomed-gallery__dialog')) continue;
      if (!dialog.open) continue;
      addZoomArrows(dialog);
      setTimeout(() => markThumbnail(dialog, currentZoomIndex(dialog)), 350);
    }
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });

  // Toetsen: pijl links/rechts (en omhoog/omlaag) bladeren door de foto's
  document.addEventListener('keydown', (event) => {
    const dialog = openZoomDialog();
    if (!dialog) return;
    if (['ArrowRight', 'ArrowDown', 'PageDown'].includes(event.key)) {
      event.preventDefault();
      goZoom(dialog, 1);
    } else if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(event.key)) {
      event.preventDefault();
      goZoom(dialog, -1);
    }
  });

  // Mobiel: niet automatisch inzoomen en vrij swipen naar de volgende foto.
  // De aanraak-afhandeling van Horizon (drag-zoom) blokkeert het swipen; die slaan we over.
  ['touchstart', 'touchmove', 'touchend'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        if (!isMobile()) return;
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest('.dialog-zoomed-gallery__dialog drag-zoom-wrapper')) event.stopPropagation();
      },
      { capture: true, passive: true }
    );
  });

  // Mobiel: na het swipen het juiste miniatuurtje markeren en de scherpe foto laden
  let zoomScrollTimer = 0;
  document.addEventListener(
    'scroll',
    (event) => {
      const list = event.target instanceof Element ? event.target : null;
      if (!list?.classList.contains('dialog-zoomed-gallery')) return;
      clearTimeout(zoomScrollTimer);
      zoomScrollTimer = window.setTimeout(() => {
        const dialog = list.closest('dialog');
        if (dialog) markThumbnail(dialog, currentZoomIndex(dialog));
      }, 80);
    },
    true
  );

  // Desktop: na scrollen de pijlen bijwerken
  document.addEventListener(
    'scroll',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('dialog-zoomed-gallery__dialog')) return;
      clearTimeout(zoomScrollTimer);
      zoomScrollTimer = window.setTimeout(() => markThumbnail(dialog, currentZoomIndex(dialog)), 80);
    },
    true
  );

  document.addEventListener('change', async (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('vm-card-buy select')) return;
    const buy = select.closest('vm-card-buy');
    const card = buy?.closest('product-card');
    if (!buy || !card) return;

    const selects = [...buy.querySelectorAll('select')];
    const index = selects.indexOf(select);
    const selection = selects.map((s) => s.value);
    const product = await loadProduct(buy.getAttribute('data-product-url'));
    if (!product || !Array.isArray(product.variants)) return;

    const optionName = String((product.options || [])[index]?.name || product.options?.[index] || '').toLowerCase().trim();
    const isColor = COLOR_NAMES.includes(optionName);
    const variants = product.variants;
    const exact = variants.find((v) => v.options.every((value, i) => value === selection[i]));
    const sameColor = isColor ? variants.filter((v) => v.options[index] === select.value) : [];

    const mediaVariant = exact?.featured_media ? exact : sameColor.find((v) => v.featured_media);
    if (mediaVariant) showMedia(card, mediaVariant.featured_media.id);

    const linkVariant = exact || sameColor.find((v) => v.available) || sameColor[0];
    if (linkVariant) setLinks(card, linkVariant.id);
  });
})();
